import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, CheckCircle, XCircle, Loader2, PoundSterling, Crown, Shield, Award, Gem, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_CONFIG = {
  bronze:   { icon: Shield, color: 'from-amber-700 to-amber-600',   border: 'border-amber-700/40',   accent: 'text-amber-500',  popular: false },
  silver:   { icon: Award,  color: 'from-slate-400 to-slate-300',   border: 'border-slate-400/40',   accent: 'text-slate-300',  popular: false },
  gold:     { icon: Crown,  color: 'from-yellow-500 to-amber-400',  border: 'border-yellow-500/40',  accent: 'text-yellow-400', popular: true  },
  platinum: { icon: Gem,    color: 'from-cyan-300 to-indigo-400',   border: 'border-cyan-300/40',    accent: 'text-cyan-300',   popular: false },
};

function SponsorsPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [tiers, setTiers] = useState([]);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    fetch(`${API}/sponsor-packages`)
      .then(r => r.json())
      .then(data => {
        setTiers(data.tiers || []);
      })
      .catch(err => console.error('Failed to load packages:', err));
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      if (attempts >= maxAttempts) { setPaymentStatus('timeout'); setPolling(false); return; }
      attempts++;
      try {
        const res = await fetch(`${API}/checkout-status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === 'paid') { setPaymentStatus('paid'); setPolling(false); return; }
        if (data.status === 'expired') { setPaymentStatus('expired'); setPolling(false); return; }
        setTimeout(poll, 2000);
      } catch (err) { setTimeout(poll, 2000); }
    };
    poll();
  }, [sessionId]);

  const validateForm = () => {
    if (!clientName.trim()) { setError('Please enter your name'); return false; }
    if (!clientEmail.trim() || !clientEmail.includes('@')) { setError('Please enter a valid email'); return false; }
    setError('');
    return true;
  };

  const handleCheckout = async (packageId) => {
    if (!validateForm()) return;
    setLoading(packageId);
    setError('');
    try {
      const res = await fetch(`${API}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, client_name: clientName, client_email: clientEmail, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Payment error');
      if (data.url) window.location.href = data.url;
    } catch (err) { setError(err.message); } finally { setLoading(null); }
  };

  // Success / Polling UI
  if (sessionId) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 pt-28 pb-16">
          <div className="max-w-md w-full bg-slate-900 border border-slate-700 rounded-2xl p-8 text-center" data-testid="payment-result-card">
            {polling && (
              <>
                <Loader2 className="mx-auto mb-4 text-cyan-400 animate-spin" size={48} />
                <h2 className="text-xl font-bold text-white mb-2">Verifying Payment...</h2>
                <p className="text-gray-400 text-sm">Please wait while we confirm your payment.</p>
              </>
            )}
            {paymentStatus === 'paid' && (
              <>
                <CheckCircle className="mx-auto mb-4 text-green-400" size={48} />
                <h2 className="text-xl font-bold text-white mb-2" data-testid="payment-success-heading">Payment Successful!</h2>
                <p className="text-gray-400 text-sm mb-6">Thank you for sponsoring Paisley.org.uk. We'll be in touch shortly.</p>
                <a href="/sponsors" className="inline-block px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors" data-testid="back-to-sponsors-link">Back to Sponsors</a>
              </>
            )}
            {paymentStatus === 'expired' && (
              <>
                <XCircle className="mx-auto mb-4 text-red-400" size={48} />
                <h2 className="text-xl font-bold text-white mb-2">Payment Expired</h2>
                <p className="text-gray-400 text-sm mb-6">Your payment session has expired. Please try again.</p>
                <a href="/sponsors" className="inline-block px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors">Try Again</a>
              </>
            )}
            {paymentStatus === 'timeout' && (
              <>
                <XCircle className="mx-auto mb-4 text-yellow-400" size={48} />
                <h2 className="text-xl font-bold text-white mb-2">Status Unknown</h2>
                <p className="text-gray-400 text-sm mb-6">We couldn't confirm your payment status. Please check your email for a confirmation.</p>
                <a href="/sponsors" className="inline-block px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors">Back to Sponsors</a>
              </>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <SEO
        title="Become a Sponsor | Paisley.org.uk"
        description="Support Paisley.org.uk and promote your brand. Bronze, Silver, Gold, and Platinum partnership tiers with logo, social, event, and feature blog benefits."
        keywords="Paisley sponsor, advertise Paisley, Paisley.org.uk sponsorship, Bronze Silver Gold Platinum partner"
        url="/sponsors"
      />
      <Header />
      <main className="flex-1 pt-28 pb-16">
        {/* Hero */}
        <section className="max-w-5xl mx-auto px-6 text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-cyan-500/20 rounded-full">
            <CreditCard className="text-cyan-400" size={18} />
            <span className="text-cyan-300 font-medium text-sm">Partnership & Sponsorship</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="sponsors-page-heading">
            Become a <span className="text-cyan-400">Partner</span>
          </h1>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            Support Paisley.org.uk and promote your brand to thousands of visitors. Choose the annual partnership tier that's right for you.
          </p>
        </section>

        {/* Client Info Form */}
        <section className="max-w-5xl mx-auto px-6 mb-10">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6">
            <h2 className="text-base font-semibold text-white mb-4">Your Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Full Name</label>
                <input type="text" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="John Smith"
                  className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-600 text-white text-sm placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors"
                  data-testid="sponsor-name-input" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Email Address</label>
                <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="john@company.com"
                  className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-600 text-white text-sm placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors"
                  data-testid="sponsor-email-input" />
              </div>
            </div>
            {error && <p className="mt-3 text-red-400 text-sm" data-testid="sponsor-form-error">{error}</p>}
          </div>
        </section>

        {/* ===== PARTNERSHIP TIERS ===== */}
        <section className="max-w-5xl mx-auto px-6 mb-16">
          <h2 className="text-lg font-bold text-white mb-2">Partnership Tiers</h2>
          <p className="text-gray-400 text-sm mb-6">Annual partnerships with ongoing benefits and premium visibility.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="tier-cards">
            {tiers.map(tier => {
              const cfg = TIER_CONFIG[tier.id] || TIER_CONFIG.bronze;
              const Icon = cfg.icon;
              return (
                <div
                  key={tier.id}
                  className={`relative bg-slate-900 border ${cfg.border} rounded-2xl p-6 flex flex-col hover:shadow-lg hover:shadow-slate-900/50 transition-all duration-300`}
                  data-testid={`tier-card-${tier.id}`}
                >
                  {cfg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-cyan-500 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider">Most Popular</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-4 mt-1">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${cfg.color} flex items-center justify-center`}>
                      <Icon size={20} className="text-white" />
                    </div>
                    <h3 className="text-white font-bold">{tier.name}</h3>
                  </div>

                  <div className="flex items-baseline gap-1 mb-5">
                    <span className="text-3xl font-bold text-white flex items-center"><PoundSterling size={22} className="mr-0.5" />{tier.amount}</span>
                    <span className="text-gray-500 text-sm">/{tier.period}</span>
                  </div>

                  <ul className="space-y-2.5 mb-6 flex-1">
                    {tier.benefits.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <Check size={15} className={`${cfg.accent} mt-0.5 shrink-0`} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleCheckout(tier.id)}
                    disabled={!!loading}
                    className={`w-full py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                      cfg.popular
                        ? 'bg-cyan-500 hover:bg-cyan-600 text-white'
                        : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-600'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid={`tier-pay-btn-${tier.id}`}
                  >
                    {loading === tier.id ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                    Select {tier.name}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* ===== ONE-TIME PACKAGES + CUSTOM AMOUNT removed Jun 2026 —
             only annual partnership tiers are sold from /sponsors now. ===== */}
      </main>
      <Footer />
    </div>
  );
}

export default SponsorsPage;
